import { useRef, type KeyboardEvent } from 'react';

export interface AnswerOptionItem {
  id: string;
  label: string;
}

interface AnswerOptionGroupProps {
  legend: string;
  options: readonly AnswerOptionItem[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
  name: string;
}

/**
 * Große, vollständig anklickbare Antwortkarten mit Radio-Semantik (FR-TEST-004, A11Y-003).
 * Nutzbar für Testfragen und Kontextfragen gleichermaßen.
 */
export function AnswerOptionGroup({
  legend,
  options,
  selectedId,
  onSelect,
  name,
}: AnswerOptionGroupProps) {
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  function focusOption(index: number): void {
    const option = options[(index + options.length) % options.length];
    if (!option) return;
    buttonRefs.current.get(option.id)?.focus();
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      focusOption(index + 1);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      focusOption(index - 1);
    }
  }

  return (
    <fieldset className="answer-fieldset">
      <legend className="visually-hidden">{legend}</legend>
      <div className="answer-options" role="radiogroup" aria-label={legend}>
        {options.map((option, index) => {
          const checked = option.id === selectedId;
          return (
            <button
              key={option.id}
              ref={(node) => {
                if (node) buttonRefs.current.set(option.id, node);
                else buttonRefs.current.delete(option.id);
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              className="answer-card card"
              tabIndex={checked || (!selectedId && index === 0) ? 0 : -1}
              onClick={() => onSelect(option.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              data-testid={`answer-option-${name}-${option.id}`}
            >
              <span className="answer-card__marker" aria-hidden="true" />
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
